/**
 * Display labels for standard grid columns, resolved by stable key.
 *
 * `grid-column-config.ts` keeps its English `label` — it is the default, and it
 * still feeds `standard-fields.ts`, the CSV import wizard and CSV export
 * headers. This module adds the translated display path on top of it, keyed by
 * the column's `key`, which never changes: `fields.<entity>.<key>`.
 *
 * WHY THIS USES `defaultValue` (a deliberate exception to the project rule that
 * bans it): the same renderers that draw standard columns also draw *custom*
 * columns, whose labels are workspace data typed by a user. A custom column has
 * no catalog key and must never be translated, so `columnLabel` has to be safe
 * to call on any column. `defaultValue: column.label` makes the un-keyed case a
 * no-op that renders the label verbatim. The parity test
 * (`column-label.test.ts`) is what stops a *standard* column quietly relying on
 * that fallback: every key in `grid-column-config.ts` must exist in
 * `locales/en/fields.json`.
 */

import enFields from "@/lib/i18n/locales/en/fields.json";
import {
  COMPANY_GRID_COLUMNS,
  DEAL_BILLING_COLUMN,
  DEAL_GRID_COLUMNS,
  DEAL_PRODUCTS_COLUMN,
  PEOPLE_GRID_COLUMNS,
} from "@/lib/grid/grid-column-config";

/** The three grids whose standard columns have a translated field catalog. */
export type GridEntity = "people" | "companies" | "deals";

/** The backend object type the grids pass around, mapped onto a catalog entity. */
export type GridObjectType = "Contact" | "Account" | "Deal";

const OBJECT_TYPE_ENTITY: Record<GridObjectType, GridEntity> = {
  Contact: "people",
  Account: "companies",
  Deal: "deals",
};

/**
 * Entity for a grid's `objectType` prop, or null when a table has none (the
 * Notes table renders a fixed column set with no field catalog). A null entity
 * makes `columnLabel` render the English label unchanged.
 */
export function gridEntityForObjectType(
  objectType: GridObjectType | null | undefined,
): GridEntity | null {
  return objectType ? OBJECT_TYPE_ENTITY[objectType] ?? null : null;
}

/** Minimal shape `columnLabel` needs — `ColumnDef` and `StandardField` both fit. */
export interface LabelledColumn {
  key: string;
  label: string;
}

/** The `t` from `useTranslation()`, narrowed to what this module calls. */
export type LabelTranslator = (key: string, options?: Record<string, unknown>) => string;

/**
 * The display label for one grid column.
 *
 * Standard columns resolve `fields.<entity>.<key>`; custom columns (and any
 * table with no entity) fall through to the authored English label.
 */
export function columnLabel(
  entity: GridEntity | null | undefined,
  column: LabelledColumn,
  t: LabelTranslator,
): string {
  if (!entity) return column.label;
  return t(`fields.${entity}.${column.key}`, { defaultValue: column.label });
}

/** The grid arrays each entity's standard columns come from, in grid order. */
const GRID_COLUMNS: Record<GridEntity, { key: string }[]> = {
  people: PEOPLE_GRID_COLUMNS,
  companies: COMPANY_GRID_COLUMNS,
  deals: [...DEAL_GRID_COLUMNS, DEAL_BILLING_COLUMN, DEAL_PRODUCTS_COLUMN],
};

/**
 * Grid column keys with no entry in `locales/en/fields.json`, per entity.
 * Drives the parity test, mirroring `unmappedGridColumnKeys` in
 * `standard-fields.ts`: a column added to a grid without a field key would
 * silently fall back to English for every language.
 */
export function unlabeledGridColumnKeys(): Record<string, string[]> {
  const catalog = enFields as Record<string, Record<string, string>>;
  const result: Record<string, string[]> = {};
  for (const entity of Object.keys(GRID_COLUMNS) as GridEntity[]) {
    const missing = GRID_COLUMNS[entity]
      .filter(col => !catalog[entity]?.[col.key])
      .map(col => col.key);
    if (missing.length) result[entity] = missing;
  }
  return result;
}
